import { ref, reactive, watch } from 'vue';
import { useEnums } from './enums';
export function useCalc(props, rootElRef) {
    const { Step, StepLabels, StepNumbers, StepList, ButtonStrings, AgeFee, ServiceFee, Fees, MaritalStatus, Accommodation, TooltipText, } = useEnums(props);
    const activeStep = ref(Step.AgeAndMarital);
    const maritalStatus = ref(null);
    const careForBoth = ref(null);
    const applicant = reactive(getDefaultApplicant());
    const coApplicant = reactive(getDefaultCoApplicant());
    const movingForward = ref(true);
    function getDefaultApplicant() {
        return {
            age: null,
            service: null,
            accommodation: null,
            fee: null,
            netIncome: 0,
            allowance: 0,
            capital: 0,
            lawyerCost: 0,
            rent: 0,
            assessedValue: 0,
            area: 0,
            interestExpense: 0,
            livingArea: 0,
            apartmentFee: 0,
            heating: true,
            interestCost: 0,
        };
    }
    function getDefaultCoApplicant() {
        return {
            age: null,
            service: null,
            accommodation: null,
            fee: null,
            netIncome: 0,
            allowance: 0,
            capital: 0,
            lawyerCost: 0,
            rent: 0,
        };
    }
    function resetForm() {
        maritalStatus.value = null;
        careForBoth.value = null;
        const defApplicant = getDefaultApplicant();
        const defCoApplicant = getDefaultCoApplicant();
        Object.keys(defApplicant).forEach((key) => {
            applicant[key] = defApplicant[key];
        });
        Object.keys(defCoApplicant).forEach((key) => {
            coApplicant[key] = defCoApplicant[key];
        });
        activeStep.value = Step.AgeAndMarital;
        window.scrollTo({ top: 200 });
    }
    function snum(step) {
        return StepNumbers[typeof step === 'string' ? step : step.value];
    }
    // Sets defaults after step change
    watch(() => activeStep.value, (nextStep, prevStep) => {
        const nextStepNum = snum(nextStep);
        movingForward.value = nextStepNum > snum(prevStep);
        if (nextStepNum >= snum(Step.Accommodation)) {
            if (!careForBoth.value) {
                coApplicant.accommodation = null;
                coApplicant.service = null;
            }
            if (is.applicantSpecialCare()) {
                applicant.accommodation = Accommodation.special;
            }
            if (is.coApplicantSpecialCare()) {
                coApplicant.accommodation = Accommodation.special;
            }
        }
        if (nextStepNum >= snum(Step.IncomeAndExpenditure)) {
            if (is.applicantSpecialCare()) {
                applicant.fee = Fees.six;
            }
            if (is.coApplicantSpecialCare()) {
                coApplicant.fee = Fees.six;
            }
        }
        if (nextStepNum >= snum(Step.Calculation)) {
            if (is.domesticCare()) {
                coApplicant.accommodation = applicant.accommodation;
            }
        }
    });
    function gotoStep(step, scrollToTop = false) {
        const stepNum = snum(step);
        // Check if all required fields are filled
        if (stepNum > snum(Step.AgeAndMarital) && is.unfulfilled.age()) {
            return;
        }
        if (stepNum > snum(Step.TypeOfService) && is.unfulfilled.service()) {
            return;
        }
        if (stepNum > snum(Step.Accommodation) && is.unfulfilled.accommodation()) {
            return;
        }
        if (stepNum > snum(Step.IncomeAndExpenditure) && is.unfulfilled.income()) {
            return;
        }
        activeStep.value = step;
        if (scrollToTop && rootElRef?.value) {
            window.scrollTo({
                top: window.scrollY + rootElRef.value.getBoundingClientRect().top - 200,
                behavior: 'smooth',
            });
        }
    }
    const is = {
        single: () => !maritalStatus.value || maritalStatus.value?.value === "single",
        married: () => maritalStatus.value?.value === "married",
        caringForBoth: () => !is.single() && careForBoth.value,
        applicantSpecialCare: () => applicant.service?.name === "special",
        coApplicantSpecialCare: () => !is.single() && coApplicant.service?.name === "special",
        domesticCare: () => !is.single() && applicant.service?.name === "domestic" && coApplicant.service?.name === "domestic",
        apartment: () => {
            if (is.single()) {
                return applicant.accommodation?.value === "apartment";
            }
            return applicant.accommodation?.value === "apartment" || coApplicant.accommodation?.value === "apartment";
        },
        house: () => {
            if (is.single()) {
                return applicant.accommodation?.value === "house";
            }
            return applicant.accommodation?.value === "house" || coApplicant.accommodation?.value === "house";
        },
        rent: () => {
            if (is.single()) {
                return applicant.accommodation?.value === "rent";
            }
            return applicant.accommodation?.value === "rent" || coApplicant.accommodation?.value === "rent";
        },
        optionAvailable: {
            applicantAccommodation: (key) => {
                return is.applicantSpecialCare() && key === "special"
                    || !is.applicantSpecialCare() && key !== "special";
            },
            applicantFee: (key) => {
                return is.applicantSpecialCare() && key === "six"
                    || !is.applicantSpecialCare();
            },
            coApplicantAccommodation: (key) => {
                return is.coApplicantSpecialCare() && key === "special"
                    || !is.coApplicantSpecialCare() && key !== "special";
            },
            coApplicantFee: (key) => {
                return is.coApplicantSpecialCare() && key === "six"
                    || !is.coApplicantSpecialCare();
            },
            coApplicantRentField: () => {
                return !is.domesticCare() && (coApplicant.accommodation?.value === "rent" || is.coApplicantSpecialCare());
            }
        },
        unfulfilled: {
            age: () => {
                if (is.single() || !is.caringForBoth()) {
                    return !applicant.age || !maritalStatus.value;
                }
                return !applicant.age || !coApplicant.age || !maritalStatus.value;
            },
            service: () => {
                if (is.single() || !is.caringForBoth()) {
                    return !applicant.service;
                }
                return !applicant.service || !coApplicant.service;
            },
            accommodation: () => {
                if (is.single() || !is.caringForBoth() || is.domesticCare()) {
                    return !applicant.accommodation;
                }
                return !applicant.accommodation || !coApplicant.accommodation;
            },
            house: () => {
                return is.house() ? applicant.assessedValue <= 0 || applicant.area <= 0 : false;
            },
            apartment: () => {
                if (is.apartment()) {
                    if (!applicant.heating) {
                        return applicant.apartmentFee <= 0 || applicant.livingArea <= 0;
                    }
                    return applicant.apartmentFee <= 0;
                }
                return false;
            },
            applicantIncome: () => {
                return applicant.netIncome <= 0 || (applicant.fee?.value || 0) <= 0;
            },
            coApplicantIncome: () => {
                return coApplicant.netIncome <= 0 || (coApplicant.fee?.value || 0) <= 0;
            },
            rent: () => {
                return is.applicantSpecialCare() || applicant.accommodation?.value === 'rent' ? applicant.rent <= 0 : false;
            },
            coApplicantRent: () => {
                return is.coApplicantSpecialCare() || coApplicant.accommodation?.value === 'rent' ? coApplicant.rent <= 0 : false;
            },
            income: () => {
                if (is.single() || !is.caringForBoth() && maritalStatus.value?.value === 'partner') {
                    return is.unfulfilled.applicantIncome()
                        || is.unfulfilled.house()
                        || is.unfulfilled.rent()
                        || is.unfulfilled.apartment();
                }
                if (!is.caringForBoth()) {
                    return is.unfulfilled.applicantIncome()
                        || coApplicant.netIncome <= 0
                        || is.unfulfilled.house()
                        || is.unfulfilled.apartment()
                        || is.unfulfilled.rent();
                }
                return is.unfulfilled.applicantIncome()
                    || is.unfulfilled.coApplicantIncome()
                    || is.unfulfilled.rent()
                    || is.unfulfilled.coApplicantRent()
                    || is.unfulfilled.house()
                    || is.unfulfilled.apartment();
            }
        }
    };
    const compute = {
        serviceFee: () => {
            return is.single() ? applicant.service?.single : applicant.service?.value;
        },
        monthlyCapital: (capital) => {
            return Math.round(capital / 12);
        },
        careFee: (person) => {
            const fee = compute.feeLeftover(person);
            if (fee < (person.fee?.value || 0) && fee > 0) {
                return fee;
            }
            return compute.feeLeftover(person) < 0 ? 0 : person.fee?.value;
        },
        allIncome: (person) => {
            if ((is.single() || is.coApplicantSpecialCare() || is.applicantSpecialCare() || is.domesticCare() || !is.caringForBoth()) &&
                !is.married()) {
                return Math.round(person.netIncome
                    + person.allowance
                    + compute.monthlyCapital(person.capital));
            }
            return Math.round((applicant.netIncome
                + applicant.allowance
                + compute.monthlyCapital(applicant.capital)
                + coApplicant.netIncome
                + compute.monthlyCapital(coApplicant.capital)) / 2);
        },
        allExpenses: (person) => {
            let total = person.lawyerCost
                + (person.age?.value || 0)
                + compute.rentFee(person);
            return Math.round(total);
        },
        rentFee: (person) => {
            let total = 0;
            if (is.rent() || is.applicantSpecialCare() || is.coApplicantSpecialCare()) {
                if (is.domesticCare() || !is.caringForBoth() && !is.applicantSpecialCare() && !is.single()) {
                    total += applicant.rent / 2;
                }
                else {
                    total += person.rent;
                }
            }
            if (person.accommodation?.value === "house") {
                const taxes = applicant.assessedValue * 0.75;
                const taxesYear = taxes >= 9525 ? 9525 : taxes;
                const houseCost = applicant.area * 666;
                const interestExpense = applicant.interestExpense * 0.7;
                if (is.single() || is.coApplicantSpecialCare() || is.applicantSpecialCare()) {
                    total += (taxesYear / 12) + interestExpense + (houseCost / 12);
                }
                else {
                    total += (taxesYear / 24) + (interestExpense / 2) + (houseCost / 24);
                }
            }
            if (person.accommodation?.value === "apartment") {
                let interestCost = applicant.interestCost * 0.7;
                let heating = applicant.livingArea * 244 / 12;
                if (is.single() || is.coApplicantSpecialCare() || is.applicantSpecialCare()) {
                    total += applicant.apartmentFee + interestCost;
                }
                else {
                    total += (applicant.apartmentFee / 2) + (interestCost / 2);
                    heating = heating / 2;
                }
                if (!applicant.heating) {
                    total += heating;
                }
            }
            return Math.round(total);
        },
        feeLeftover: (person) => {
            return compute.allIncome(person)
                - compute.allExpenses(person)
                - ((is.single() ? compute.serviceFee() : person.service?.value) || 0);
        }
    };
    return {
        props,
        activeStep,
        maritalStatus,
        careForBoth,
        applicant,
        coApplicant,
        movingForward,
        resetForm,
        gotoStep,
        is,
        compute,
        Step,
        StepLabels,
        TooltipText,
        StepNumbers,
        StepList,
        ButtonStrings,
        AgeFee,
        ServiceFee,
        Fees,
        MaritalStatus,
        Accommodation,
    };
}
